import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, ChevronRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import NewsCard from '../components/news/NewsCard';
import NewsTicker from '../components/news/NewsTicker';
import ProductCard from '../components/products/ProductCard';
import NewsletterForm from '../components/common/NewsletterForm';
import CostCalculatorWidget from '../components/common/CostCalculatorWidget';
import SupportInstitutions from '../components/common/SupportInstitutions';
import { newsAPI, productsAPI, servicesAPI, seedAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const categories = [
  { name: 'Imigração', slug: 'Imigração' },
  { name: 'Vistos', slug: 'Vistos' },
  { name: 'AIMA', slug: 'AIMA' },
  { name: 'Trabalho', slug: 'Trabalho' },
  { name: 'Custo de Vida', slug: 'Custo de Vida' },
  { name: 'Curiosidades', slug: 'Curiosidades' },
];

// Skeleton shown while news data is still loading (defined outside component
// so React doesn't recreate it on every render — see no-unstable-nested-components).
const HeroSkeleton = () => (
  <section className="py-8 md:py-12" data-testid="hero-skeleton">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 aspect-[16/10] bg-[#E9E5D6] rounded-sm animate-pulse" />
        <div className="lg:col-span-4 flex flex-col gap-6">
          <div className="aspect-[3/1] bg-[#E9E5D6] rounded-sm animate-pulse" />
          <div className="aspect-[3/1] bg-[#E9E5D6] rounded-sm animate-pulse" />
        </div>
      </div>
    </div>
  </section>
);

export const HomePage = () => {
  const [news, setNews] = useState([]);
  const [breakingNews, setBreakingNews] = useState([]);
  const [featuredNews, setFeaturedNews] = useState([]);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);

  // Initial load: hydrate from localStorage cache so the page renders instantly
  // even when the API is slow or temporarily unreachable. The cache is then refreshed
  // in the background via loadData().
  const [loading, setLoading] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('home:cache:v1') || 'null');
      if (cached?.news?.length) return false;
    } catch (_) { /* no-op */ }
    return true;
  });

  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem('home:cache:v1') || 'null');
      if (cached?.news?.length) {
        setNews(cached.news || []);
        setBreakingNews(cached.breakingNews || []);
        setFeaturedNews(cached.featuredNews || []);
        setProducts(cached.products || []);
        setServices(cached.services || []);
      }
    } catch (_) { /* no-op */ }
  }, []);

  const loadData = useCallback(async (retried = false) => {
    // First seed the database if needed (best-effort)
    try { await seedAPI.seed(); } catch (e) { /* already seeded */ }

    let results;
    try {
      results = await Promise.allSettled([
        newsAPI.getAll({ limit: 12 }),
        newsAPI.getBreaking(),
        productsAPI.getAll({ featured: true }),
        servicesAPI.getAll({ featured: true }),
      ]);
    } catch (error) {
      console.error('Unexpected error:', error);
      setLoading(false);
      return;
    }

    const [newsRes, breakingRes, productsRes, servicesRes] = results;
    const allFailed = results.every(r => r.status === 'rejected');

    if (allFailed && !retried) {
      // One silent retry after a short delay (backend warmup / transient blips)
      setTimeout(() => loadData(true), 1500);
      return;
    }

    if (allFailed) {
      console.warn('All home endpoints failed after retry. Keeping cached state.');
      setLoading(false);
      return;
    }

    const allNews = newsRes.status === 'fulfilled' ? newsRes.value.data : [];
    const breaking = breakingRes.status === 'fulfilled' ? breakingRes.value.data : [];
    const featured = allNews.filter(n => n.is_featured).slice(0, 3);
    const prods = productsRes.status === 'fulfilled' ? productsRes.value.data.slice(0, 3) : [];
    const srvs = servicesRes.status === 'fulfilled' ? servicesRes.value.data.slice(0, 3) : [];

    setNews(allNews);
    setBreakingNews(breaking);
    setFeaturedNews(featured);
    setProducts(prods);
    setServices(srvs);
    setLoading(false);

    // Persist for instant subsequent loads
    try {
      localStorage.setItem('home:cache:v1', JSON.stringify({
        news: allNews, breakingNews: breaking, featuredNews: featured,
        products: prods, services: srvs, ts: Date.now(),
      }));
    } catch (_) { /* quota or disabled */ }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const mainFeatured = featuredNews[0];
  const sideFeatured = featuredNews.slice(1, 3);
  const latestNews = news.filter(n => !n.is_featured).slice(0, 6);
  const hasContent = mainFeatured || sideFeatured.length > 0 || latestNews.length > 0;

  return (
    <Layout>
      {/* Breaking News Ticker */}
      {breakingNews.length > 0 && <NewsTicker news={breakingNews} />}

      {/* Hero Section */}
      {loading && !hasContent ? <HeroSkeleton /> : (
        <section className="py-8 md:py-12" data-testid="hero-section">
          <div className="container mx-auto px-4">
            {mainFeatured ? (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8">
                  <NewsCard news={mainFeatured} variant="featured" />
                </div>
                <div className="lg:col-span-4 flex flex-col gap-6">
                  {sideFeatured.map((item) => (
                    <NewsCard key={item.id} news={item} variant="horizontal" />
                  ))}
                </div>
              </div>
            ) : !loading ? (
              <div className="text-center py-16 px-6 bg-[#F5F0E8] rounded-sm">
                <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-2">
                  Conteúdo a chegar em breve
                </h2>
                <p className="text-[#5C5C5C] font-['Inter'] text-sm max-w-md mx-auto">
                  Estamos a preparar artigos exclusivos para si. Volte em breve ou subscreva a nossa newsletter para ser notificado.
                </p>
              </div>
            ) : null}
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="py-6 border-y border-[#D6D2C4]" data-testid="categories-section">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <Link key={cat.slug} to={`/categoria/${encodeURIComponent(cat.slug)}`}>
                <Badge
                  variant="outline"
                  className="px-4 py-2 font-['Montserrat'] text-sm font-medium border-[#0F5A34] text-[#0F5A34] hover:bg-[#0F5A34] hover:text-white transition-colors cursor-pointer"
                >
                  {cat.name}
                </Badge>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Second block: Calculator widget (left) + Latest News sidebar (right) */}
      <section className="py-12 md:py-16" data-testid="second-block">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* LEFT: Calculator widget */}
            <div className="lg:col-span-8">
              <CostCalculatorWidget />
            </div>

            {/* RIGHT: Latest news sidebar */}
            <aside className="lg:col-span-4" data-testid="latest-news-sidebar">
              <div className="lg:sticky lg:top-24">
                <div className="flex items-center justify-between mb-5 pb-3 border-b-2 border-[#0F5A34]">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={20} className="text-[#0F5A34]" />
                    <h2 className="font-['Playfair_Display'] text-xl md:text-2xl font-bold text-[#1A1A1A]">
                      Últimas Notícias
                    </h2>
                  </div>
                  <Link to="/categoria/Notícias" className="text-xs font-medium text-[#0F5A34] hover:underline inline-flex items-center gap-0.5">
                    Ver todas <ChevronRight size={14} />
                  </Link>
                </div>
                <div className="flex flex-col gap-3">
                  {latestNews.map((item) => (
                    <NewsCard key={item.id} news={item} variant="horizontal" />
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Products Section */}
      {products.length > 0 && (
        <section className="py-12 md:py-16 bg-white" data-testid="products-section">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <Badge className="bg-[#DFAF3A] text-[#1A1A1A] font-['Montserrat'] text-xs uppercase tracking-wider mb-2">
                  Produtos Digitais
                </Badge>
                <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-[#1A1A1A]">
                  Recursos Para Sua Jornada
                </h2>
              </div>
              <Link to="/produtos">
                <Button variant="outline" className="font-['Montserrat'] text-sm border-[#0F5A34] text-[#0F5A34] hover:bg-[#0F5A34] hover:text-white">
                  Ver Todos
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter Section */}
      <NewsletterForm />

      {/* Support Institutions */}
      <SupportInstitutions />
    </Layout>
  );
};

export default HomePage;
